import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapFullscreenToggleButton from '@/pages/Business/pages/Shippings/pages/RouteBuilder/components/MapFullscreenToggleButton';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { TrajectoryPoint } from '../models';
import { VisitStatus, VisitStatusColors, RouteStatus } from '../enums';
import {
  createBaseMarkerIcon,
  createRouteVisitPinIcon,
} from '../utils/routeMapLeafletIcons';
import { LiveDriverMapLayer } from '@/pages/Business/shared/driver-gps';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import {
  computeSpiderPlacements,
  makeCoordKey,
  SPIDER_ANCHOR_FILL,
  SPIDER_ANCHOR_STROKE,
  SPIDER_LEG_COLOR,
} from '@/utils/leaflet/marker-spider';

const ROUTE_PIN_GREEN = '#22c55e';

const markerStyles = `
  .custom-route-marker,
  .custom-base-marker {
    background: transparent !important;
    border: none !important;
  }
  .custom-route-marker > div,
  .custom-base-marker > div {
    pointer-events: auto !important;
  }
`;

if (
  typeof document !== 'undefined' &&
  !document.getElementById('monitoring-route-map-marker-styles')
) {
  const style = document.createElement('style');
  style.id = 'monitoring-route-map-marker-styles';
  style.textContent = markerStyles;
  document.head.appendChild(style);
}

interface RouteMapVisitInput {
  latitude: string | null;
  longitude: string | null;
  status: VisitStatus;
  order: number;
  recipient?: string;
  address?: string;
  isBase?: boolean;
  shippingNumber?: number;
}

interface RouteMapFocusTarget {
  latitude: number;
  longitude: number;
  zoom?: number;
}

interface RouteMapProps {
  trajectory: TrajectoryPoint[];
  visits?: RouteMapVisitInput[];
  height?: string;
  focusTarget?: RouteMapFocusTarget | null;
  onFocusTargetConsumed?: () => void;
  liveDriverId?: string | null;
  liveRouteStatus?: RouteStatus;
  driverName?: string;
}

function appendPopupLine(container: HTMLDivElement, text: string): void {
  const row = document.createElement('div');
  row.textContent = text;
  container.appendChild(row);
}

function buildDeliveryStopPopupContent(
  visit: RouteMapVisitInput & { latitude: string; longitude: string },
  deliverySequence: number,
  shippingBasePath: string
): HTMLDivElement {
  const root = document.createElement('div');
  root.className = 'monitoring-route-popup text-sm leading-snug';

  appendPopupLine(root, `Parada #${deliverySequence}`);
  if (visit.status) {
    appendPopupLine(root, `Estado: ${visit.status}`);
  }
  if (visit.recipient) {
    appendPopupLine(root, `Destinatario: ${visit.recipient}`);
  }
  if (visit.address) {
    appendPopupLine(root, `Dirección: ${visit.address}`);
  }

  const shippingNum = visit.shippingNumber ?? 0;
  if (shippingNum <= 0) {
    return root;
  }

  const href = `${shippingBasePath}/${shippingNum}`;
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = `Ver envío — ID: ${shippingNum}`;
  link.style.color = '#2563eb';
  link.style.textDecoration = 'underline';
  link.style.marginTop = '6px';
  link.style.display = 'inline-block';
  link.style.cursor = 'pointer';

  root.appendChild(link);
  return root;
}

const RouteMap = ({
  trajectory,
  visits = [],
  height = 'h-96',
  focusTarget = null,
  onFocusTargetConsumed,
  liveDriverId,
  liveRouteStatus,
  driverName,
}: RouteMapProps) => {
  const { showNotification } = useNotification();
  const { shippingBasePath } = useMonitoringIntegration();
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const focusTargetRef = useRef<RouteMapFocusTarget | null>(null);
  focusTargetRef.current = focusTarget;
  const onFocusTargetConsumedRef = useRef(onFocusTargetConsumed);
  onFocusTargetConsumedRef.current = onFocusTargetConsumed;
  const cooperativeWheelThrottleRef = useRef(0);
  const scrollHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [scrollZoomHintVisible, setScrollZoomHintVisible] = useState(false);

  const handleMapFullscreenError = useCallback(
    (message: string) => {
      showNotification(message, NotificationSeverity.warning);
    },
    [showNotification]
  );

  useEffect(() => {
    const container = mapRef.current;
    if (!container) {
      return undefined;
    }

    if (mapInstance.current) {
      try {
        mapInstance.current.remove();
      } catch {
        /* ignorar */
      }
      mapInstance.current = null;
    }

    let cancelled = false;

    const safeInvalidate = () => {
      if (cancelled || !mapInstance.current) return;
      try {
        mapInstance.current.invalidateSize();
      } catch {}
    };

    try {
      const visitsWithCoords = visits.filter(
        (
          visit
        ): visit is typeof visit & { latitude: string; longitude: string } =>
          visit.latitude != null &&
          visit.longitude != null &&
          Number.isFinite(parseFloat(visit.latitude)) &&
          Number.isFinite(parseFloat(visit.longitude))
      );
      const sortedVisits = [...visitsWithCoords].sort(
        (a, b) => a.order - b.order
      );

      let mapCenter: [number, number] = [-34.603722, -58.381592];
      if (trajectory.length > 0) {
        const firstTrajectoryPoint = trajectory[0];
        mapCenter = [firstTrajectoryPoint.lat, firstTrajectoryPoint.long];
      } else if (sortedVisits.length > 0) {
        const firstVisit = sortedVisits[0];
        mapCenter = [
          parseFloat(firstVisit.latitude),
          parseFloat(firstVisit.longitude),
        ];
      }

      mapInstance.current = L.map(container, {
        center: mapCenter,
        zoom: 13,
        zoomControl: false,
        zoomAnimation: false,
        scrollWheelZoom: false,
      });
      setMapReady(true);

      const map = mapInstance.current;

      L.control.zoom({ position: 'topright' }).addTo(map);

      const mapLeafletContainer = map.getContainer();
      const onCooperativeWheel = (e: WheelEvent) => {
        const mapInst = mapInstance.current;
        if (!mapInst) {
          return;
        }
        if (e.ctrlKey) {
          e.preventDefault();
          const now = Date.now();
          if (now - cooperativeWheelThrottleRef.current < 120) {
            return;
          }
          cooperativeWheelThrottleRef.current = now;
          const delta = e.deltaY;
          if (Math.abs(delta) < 1) {
            return;
          }
          const next = mapInst.getZoom() + (delta > 0 ? -1 : 1);
          mapInst.setZoom(next);
          setScrollZoomHintVisible(false);
          if (scrollHintTimeoutRef.current) {
            clearTimeout(scrollHintTimeoutRef.current);
            scrollHintTimeoutRef.current = null;
          }
          return;
        }
        setScrollZoomHintVisible(true);
        if (scrollHintTimeoutRef.current) {
          clearTimeout(scrollHintTimeoutRef.current);
        }
        scrollHintTimeoutRef.current = setTimeout(() => {
          setScrollZoomHintVisible(false);
          scrollHintTimeoutRef.current = null;
        }, 2200);
      };
      mapLeafletContainer.addEventListener('wheel', onCooperativeWheel, {
        passive: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map);

      type VisitRow = {
        rowIndex: number;
        latitude: number;
        longitude: number;
        isBase: boolean;
        visit: (typeof sortedVisits)[number];
        deliverySequence: number;
      };

      const rows: VisitRow[] = [];
      let deliverySequence = 0;
      for (let visitIndex = 0; visitIndex < sortedVisits.length; visitIndex += 1) {
        const visit = sortedVisits[visitIndex];
        const visitLatitude = parseFloat(visit.latitude);
        const visitLongitude = parseFloat(visit.longitude);
        const isBase = Boolean(visit.isBase);
        if (isBase) {
          rows.push({
            rowIndex: rows.length,
            latitude: visitLatitude,
            longitude: visitLongitude,
            isBase: true,
            visit,
            deliverySequence: 0,
          });
          continue;
        }
        deliverySequence += 1;
        rows.push({
          rowIndex: rows.length,
          latitude: visitLatitude,
          longitude: visitLongitude,
          isBase: false,
          visit,
          deliverySequence,
        });
      }

      const spiderEntries = rows.map((row) => ({
        index: row.rowIndex,
        latitude: row.latitude,
        longitude: row.longitude,
      }));
      const placementMap = computeSpiderPlacements(spiderEntries);

      const anchorKeysForSpider = new Set<string>();
      for (let spiderEntryIndex = 0; spiderEntryIndex < spiderEntries.length; spiderEntryIndex += 1) {
        const spiderEntry = spiderEntries[spiderEntryIndex];
        const spiderPlacement = placementMap.get(spiderEntry.index);
        if (!spiderPlacement?.showSpiderLeg) {
          continue;
        }
        const anchorKey = makeCoordKey(
          spiderPlacement.anchorLatitude,
          spiderPlacement.anchorLongitude
        );
        if (!anchorKeysForSpider.has(anchorKey)) {
          anchorKeysForSpider.add(anchorKey);
          L.circleMarker(
            [
              spiderPlacement.anchorLatitude,
              spiderPlacement.anchorLongitude,
            ],
            {
              radius: 9,
              color: SPIDER_ANCHOR_STROKE,
              weight: 2,
              fillColor: SPIDER_ANCHOR_FILL,
              fillOpacity: 0.45,
              interactive: false,
            }
          ).addTo(map);
        }
        L.polyline(
          [
            [
              spiderPlacement.anchorLatitude,
              spiderPlacement.anchorLongitude,
            ],
            [
              spiderPlacement.displayLatitude,
              spiderPlacement.displayLongitude,
            ],
          ],
          {
            color: SPIDER_LEG_COLOR,
            weight: 2,
            opacity: 0.92,
            interactive: false,
          }
        ).addTo(map);
      }

      for (let ri = 0; ri < rows.length; ri += 1) {
        const row = rows[ri];
        const rowPlacement = placementMap.get(row.rowIndex);
        const markerPos: L.LatLngExpression = rowPlacement
          ? [
              rowPlacement.displayLatitude,
              rowPlacement.displayLongitude,
            ]
          : [row.latitude, row.longitude];
        const visit = row.visit;

        if (row.isBase) {
          const icon = createBaseMarkerIcon();
          const marker = L.marker(markerPos, { icon }).addTo(map);
          const popupLines = [
            'Base / origen',
            visit.status && `Estado: ${visit.status}`,
            visit.address && `Dirección: ${visit.address}`,
          ].filter(Boolean);
          marker.bindPopup(popupLines.join('<br/>'));
          continue;
        }

        const statusKey = visit.status as VisitStatus;
        const pinColor =
          VisitStatusColors[statusKey]?.ballColor ?? ROUTE_PIN_GREEN;
        const icon = createRouteVisitPinIcon(row.deliverySequence, pinColor);
        const marker = L.marker(markerPos, { icon }).addTo(map);
        const popupContent = buildDeliveryStopPopupContent(
          visit,
          row.deliverySequence,
          shippingBasePath
        );
        marker.bindPopup(popupContent);
      }

      map.whenReady(() => {
        if (cancelled || mapInstance.current !== map) return;
        requestAnimationFrame(() => {
          if (cancelled || mapInstance.current !== map) return;
          safeInvalidate();
          const pendingFocus = focusTargetRef.current;
          if (
            pendingFocus != null &&
            Number.isFinite(pendingFocus.latitude) &&
            Number.isFinite(pendingFocus.longitude) &&
            mapInstance.current === map
          ) {
            map.setView(
              [pendingFocus.latitude, pendingFocus.longitude],
              pendingFocus.zoom ?? 16,
              { animate: false }
            );
            onFocusTargetConsumedRef.current?.();
            return;
          }
          if (rows.length === 0) return;
          try {
            const firstNonBase = rows.find((r) => !r.isBase);
            if (firstNonBase && mapInstance.current === map) {
              const placement = placementMap.get(firstNonBase.rowIndex);
              const centerLat = placement ? placement.displayLatitude : firstNonBase.latitude;
              const centerLng = placement ? placement.displayLongitude : firstNonBase.longitude;
              map.setView([centerLat, centerLng], 15, { animate: false });
            }
          } catch {}
        });
      });

      const resizeObserver = new ResizeObserver(() => {
        safeInvalidate();
      });
      resizeObserver.observe(container);

      return () => {
        cancelled = true;
        mapLeafletContainer.removeEventListener('wheel', onCooperativeWheel);
        if (scrollHintTimeoutRef.current) {
          clearTimeout(scrollHintTimeoutRef.current);
          scrollHintTimeoutRef.current = null;
        }
        setScrollZoomHintVisible(false);
        resizeObserver.disconnect();
        if (mapInstance.current) {
          try {
            mapInstance.current.remove();
          } catch {
            /* ya removido */
          }
          mapInstance.current = null;
        }
        setMapReady(false);
      };
    } catch (error) {
      console.error('Error inicializando mapa:', error);
      if (mapInstance.current) {
        try {
          mapInstance.current.remove();
        } catch {
          /* ignorar */
        }
        mapInstance.current = null;
      }
      return undefined;
    }
  }, [trajectory, visits, shippingBasePath]);

  useEffect(() => {
    if (focusTarget == null) {
      return undefined;
    }
    if (
      !Number.isFinite(focusTarget.latitude) ||
      !Number.isFinite(focusTarget.longitude)
    ) {
      onFocusTargetConsumed?.();
      return undefined;
    }
    const map = mapInstance.current;
    if (!map) {
      return undefined;
    }
    map.setView(
      [focusTarget.latitude, focusTarget.longitude],
      focusTarget.zoom ?? 16,
      { animate: true }
    );
    onFocusTargetConsumed?.();
    return undefined;
  }, [focusTarget, onFocusTargetConsumed]);

  return (
    <div
      ref={fullscreenContainerRef}
      className={`relative isolate ${height} w-full overflow-hidden rounded-lg`}
    >
      <MapFullscreenToggleButton
        targetRef={fullscreenContainerRef}
        onError={handleMapFullscreenError}
      />
      {scrollZoomHintVisible && (
        <div
          className='pointer-events-none absolute inset-0 z-[2100] flex items-center justify-center bg-black/45 px-6'
          aria-live='polite'
        >
          <p className='max-w-2xl text-center text-xl font-semibold leading-snug text-white sm:text-2xl sm:leading-tight'>
            Mantené presionada la tecla Ctrl y usá la rueda del mouse para
            acercar o alejar el mapa
          </p>
        </div>
      )}
      <div ref={mapRef} className='h-full w-full map-zoom-below-fullscreen-btn' />
      <LiveDriverMapLayer
        mapInstance={mapReady ? mapInstance.current : null}
        mapReady={mapReady}
        driverId={liveDriverId}
        routeStatus={liveRouteStatus}
        driverName={driverName}
      />
    </div>
  );
};

export default RouteMap;
